import { Component, inject, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';


/**
 * Confirmation dialog data.
 *
 * @param title The title of the dialog.
 * @param content The content of the dialog.
 * @param confirmButtonText The text of the confirm button.
 * @param cancelButtonText The text of the cancel button.
 */
export interface ConfirmationDialogData {
  title: string;
  content: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}


/**
 * Confirmation dialog result.
 *
 * Confirm: The user confirmed the action.
 * Cancel: The user cancelled the action.
 */
export enum ConfirmationDialogResult {
  Confirm = 'confirm',
  Cancel = 'cancel'
}


/**
 * Confirmation dialog component.
 *
 * This component is used to display a confirmation dialog to the user.
 *
 * It takes a title, content, confirm button text, and cancel button text as input.
 *
 * It emits a ConfirmationDialogResult when the user confirms or cancels the dialog.
 *
 */
@Component({
  selector: 'app-confirmation-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule
  ],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.scss'
})
export class ConfirmationDialogComponent {

  /**
   * Reference to the dialog.
   */
  readonly dialogRef = inject(MatDialogRef<ConfirmationDialogComponent>);


  /**
   * Data passed to the dialog.
   */
  data = inject(MAT_DIALOG_DATA) as ConfirmationDialogData;


  /**
   * Emits the confirmation result.
   */
  response = output<ConfirmationDialogResult>();


  /**
   * Handles the confirm action.
   */
  onConfirm() {
    this.dialogRef.close(ConfirmationDialogResult.Confirm);
  }


  /**
   * Handles the cancel action.
   */
  onCancel() {
    this.dialogRef.close(ConfirmationDialogResult.Cancel);
  }

}
